import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getApprovalQueue } from "../services/api";
import { useUserProfile } from "../hooks/useUserProfile";

interface QueueItem {
    project_id: number;
    project_name: string;
    pending_count: number;
}

const toggleBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #F18E00",
    borderRadius: "4px", padding: "8px 16px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
};

const rowStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px", border: "1px solid #e0e0e0", borderRadius: "6px",
    marginBottom: "10px", cursor: "pointer", backgroundColor: "white",
};

const ApprovalQueue: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { roles } = useUserProfile();
    const isAdmin = roles.includes("admin");

    // 2026-08-04 (v31): preserve admin mode across a round trip into
    // ApprovalDetail.tsx and back. Without this, mode always reset to "pm"
    // on remount -- ApprovalDetail's "Back to Queue" button (and its
    // post-submit navigate) just did navigate("/approvals") with no state,
    // so an admin reviewing someone else's queue would land back on their
    // own personal queue instead of staying in the admin view they came
    // from. See the matching state read/write in ApprovalDetail.tsx.
    const [mode, setMode] = useState<"pm" | "admin">(
        ((location.state as any)?.mode as "pm" | "admin") || "pm"
    );
    const [queue, setQueue] = useState<QueueItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // See Home.tsx for why this comes through route state rather than local
    // state set just before navigate() -- ApprovalDetail.tsx unmounts the
    // instant it navigates back here on a successful submit.
    const [flashMessage, setFlashMessage] = useState<string | null>(
        (location.state as any)?.flashMessage || null
    );
    useEffect(() => {
        if (!flashMessage) return;
        navigate(".", { replace: true, state: {} });
        const t = setTimeout(() => setFlashMessage(null), 4000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadQueue = useCallback(async (m: "pm" | "admin") => {
        setLoading(true);
        setError(null);
        try {
            const data = await getApprovalQueue(m);
            setQueue(data || []);
        } catch (e: any) {
            console.error("Failed to load approval queue", e);
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            let message: string;
            if (typeof detail === "string") {
                message = detail;
            } else if (status === 401) {
                message = "Your session has expired. Please refresh the page and sign in again.";
            } else if (status === 403) {
                message = "You don't have permission to view this approval queue.";
            } else if (status) {
                message = `Failed to load approval queue (HTTP ${status}). Please try again.`;
            } else {
                message = "Failed to load approval queue. Please try again.";
            }
            setError(message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadQueue(mode);
    }, [mode, loadQueue]);

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                <h1 style={{ color: "#5C5D5E", margin: 0, fontSize: "22px" }}>
                    {mode === "admin" ? "Admin Approval Queue" : "Approval Queue"}
                </h1>
                {isAdmin && (
                    <button
                        style={toggleBtnStyle}
                        onClick={() => setMode(prev => (prev === "pm" ? "admin" : "pm"))}
                    >
                        {mode === "pm" ? "Switch to Admin Queue" : "Switch to My Queue"}
                    </button>
                )}
            </div>

            {flashMessage && (
                <div style={{
                    backgroundColor: "#E8F5E9", border: "1px solid #2E7D32", color: "#2E7D32",
                    borderRadius: "6px", padding: "10px 14px", marginBottom: "16px", fontSize: "14px",
                }}>
                    ✓ {flashMessage}
                </div>
            )}

            {loading && <div style={{ color: "#666" }}>Loading...</div>}

            {!loading && error && (
                <div>
                    <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{error}</div>
                    <button onClick={() => loadQueue(mode)} style={toggleBtnStyle}>Retry</button>
                </div>
            )}

            {!loading && !error && queue.length === 0 && (
                <div style={{ color: "#999", fontSize: "14px" }}>No pending approvals.</div>
            )}

            {!loading && !error && queue.length > 0 && (
                <div>
                    <div style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>
                        Projects Pending Approval:
                    </div>
                    {queue.map(item => (
                        <div
                            key={item.project_id}
                            style={rowStyle}
                            onClick={() => navigate(`/approvals/project/${item.project_id}`, { state: { mode } })}
                        >
                            <span style={{ fontWeight: "bold", fontSize: "14px", color: "#5C5D5E" }}>
                                {item.project_name}
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: "10px", color: "#666", fontSize: "13px" }}>
                                {item.pending_count} timesheet{item.pending_count !== 1 ? "s" : ""}
                                <span style={{ color: "#F18E00", fontWeight: "bold" }}>→</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ApprovalQueue;
